import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Convert image URL (including SVG) to base64 PNG
 */
const loadImageAsBase64 = (url) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => {
            // Use a larger canvas for better quality
            const canvas = document.createElement("canvas");
            const scale = 2; // Higher quality
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            const ctx = canvas.getContext("2d");
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = reject;
        img.src = url;
    });
};

/**
 * Generate a professional PDF invoice for a transaction
 * @param {Object} transaction - The transaction data
 */
export const generateInvoicePdf = async (transaction) => {
    const doc = new jsPDF();

    // Colors matching PayCoinz theme
    const primaryColor = [245, 166, 35]; // Gold #F5A623
    const headerColor = [26, 26, 46]; // Dark navy for header background #1A1A2E
    const darkColor = [26, 26, 46]; // Dark #1A1A2E
    const textColor = [51, 51, 51]; // #333333
    const lightTextColor = [102, 102, 102]; // #666666
    const whiteColor = [255, 255, 255];

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;

    // ========== HEADER SECTION ==========
    // Dark header bar (so logo with gold coin is visible)
    doc.setFillColor(...headerColor);
    doc.rect(0, 0, pageWidth, 50, "F");

    // Try to load and add logo (SVG will be converted to PNG)
    try {
        const logoBase64 = await loadImageAsBase64("/images/logo/logo-White.svg");
        doc.addImage(logoBase64, "PNG", margin, 12, 55, 26);
    } catch (e) {
        // Fallback to text if logo fails
        doc.setFont("helvetica", "bold");
        doc.setFontSize(24);
        doc.setTextColor(...whiteColor);
        doc.text("PayCoinz", margin + 5, 30);
    }

    // Invoice label on right side
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...whiteColor);
    doc.text("WITHDRAWAL INVOICE", pageWidth - margin, 22, { align: "right" });

    // Invoice number
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(transaction?.invoice || `INV-${Date.now()}`, pageWidth - margin, 35, { align: "right" });

    // ========== INFO SECTION ==========
    let yPosition = 65;

    // Two column info layout
    doc.setFillColor(248, 249, 250);
    doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 35, 3, 3, "F");

    // Left column
    doc.setTextColor(...textColor);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Invoice Date:", margin + 8, yPosition + 12);
    doc.text("Transaction Type:", margin + 8, yPosition + 24);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(...lightTextColor);
    doc.text(formatDate(transaction?.createdAt), margin + 45, yPosition + 12);
    doc.text(transaction?.txType || "WITHDRAW", margin + 55, yPosition + 24);

    // Right column
    doc.setTextColor(...textColor);
    doc.setFont("helvetica", "bold");
    doc.text("Chain:", pageWidth / 2 + 10, yPosition + 12);
    doc.text("Status:", pageWidth / 2 + 10, yPosition + 24);

    doc.setFont("helvetica", "normal");
    doc.setTextColor(...lightTextColor);
    doc.text(transaction?.chainId?.toString() || "N/A", pageWidth / 2 + 30, yPosition + 12);

    // Status with color
    const status = transaction?.status || "SUCCESS";
    if (status === "SUCCESS") {
        doc.setTextColor(40, 167, 69); // Green
    } else if (status === "PENDING") {
        doc.setTextColor(255, 193, 7); // Yellow
    } else {
        doc.setTextColor(220, 53, 69); // Red
    }
    doc.setFont("helvetica", "bold");
    doc.text(status, pageWidth / 2 + 30, yPosition + 24);

    yPosition += 50;

    // ========== TRANSACTION DETAILS SECTION ==========
    doc.setTextColor(...primaryColor);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("Transaction Details", margin, yPosition);

    // Underline
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(1);
    doc.line(margin, yPosition + 3, margin + 52, yPosition + 3);

    yPosition += 12;

    // Format numbers properly
    const receivedAmount = parseFloat(transaction?.recivedAmount || 0).toFixed(8);
    const platformFee = parseFloat(transaction?.adminFee || 0).toFixed(8);
    const netAmount = (parseFloat(transaction?.recivedAmount || 0) - parseFloat(transaction?.adminFee || 0)).toFixed(8);

    // Transaction details table
    const transactionData = [
        ["Transaction Hash", transaction?.hash || "N/A"],
        ["From Address", transaction?.fromAddress || "N/A"],
        ["To Address", transaction?.toAddress || transaction?.to || "N/A"],
        ["Symbol", transaction?.symbol || "N/A"],
        ["Amount Received", `${receivedAmount} ${transaction?.symbol || ""}`],
        ["Platform Fee", `${platformFee} ${transaction?.symbol || ""}`],
        ["Net Amount", `${netAmount} ${transaction?.symbol || ""}`],
    ];

    autoTable(doc, {
        startY: yPosition,
        head: [],
        body: transactionData,
        theme: "plain",
        styles: {
            fontSize: 9,
            cellPadding: { top: 4, bottom: 4, left: 8, right: 8 },
        },
        columnStyles: {
            0: {
                fontStyle: "bold",
                textColor: textColor,
                cellWidth: 45,
            },
            1: {
                textColor: lightTextColor,
                cellWidth: "auto",
            },
        },
        margin: { left: margin, right: margin },
        alternateRowStyles: {
            fillColor: [248, 249, 250],
        },
    });

    yPosition = doc.lastAutoTable.finalY + 15;

    // ========== AMOUNT SUMMARY BOX ==========
    doc.setFillColor(...darkColor);
    doc.roundedRect(margin, yPosition, pageWidth - 2 * margin, 45, 4, 4, "F");

    // Left side - Total Amount
    doc.setTextColor(...whiteColor);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text("Total Received Amount", margin + 12, yPosition + 14);

    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...primaryColor);
    doc.text(
        `${receivedAmount} ${transaction?.symbol || ""}`,
        margin + 12,
        yPosition + 32
    );

    // Right side - Platform Fee
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(180, 180, 180);
    doc.text("Platform Fee:", pageWidth - margin - 60, yPosition + 16);
    doc.setTextColor(...whiteColor);
    doc.text(`${platformFee} ${transaction?.symbol || ""}`, pageWidth - margin - 60, yPosition + 28);

    // ========== FOOTER ==========
    const footerY = pageHeight - 25;

    // Gold decorative line
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(1.5);
    doc.line(margin, footerY - 10, pageWidth - margin, footerY - 10);

    // Thank you text
    doc.setTextColor(...primaryColor);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Thank you for using PayCoinz!", pageWidth / 2, footerY, { align: "center" });

    // Disclaimer
    doc.setTextColor(...lightTextColor);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text(
        "This is a computer-generated invoice and does not require a signature.",
        pageWidth / 2,
        footerY + 8,
        { align: "center" }
    );

    // ========== SAVE PDF ==========
    const fileName = `${transaction?.invoice || `Invoice_${Date.now()}`}.pdf`;
    doc.save(fileName);

    return fileName;
};

export default generateInvoicePdf;
